import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { getDashboardSummary, getKpis, getSites } from '../api';
import Sidebar from './Sidebar';
import Header from './Header';
import ExecutiveCockpit from './ExecutiveCockpit';
import DigitalTwinPanel from './DigitalTwinPanel';
import GovernanceDashboard from './GovernanceDashboard';
import AlertsPanel from './AlertsPanel';
import SiteDrilldown from './SiteDrilldown';
import EnterpriseHeatMap from './EnterpriseHeatMap';
import ThresholdConfig from './ThresholdConfig';
import NotificationToasts from './NotificationToasts';
import ReportsHub from './ReportsHub';
import AdminConsole from './AdminConsole';
import CbamModule from './CbamModule';
import OeeModule from './OeeModule';
import EmissionInventory from './EmissionInventory';
import { useNotifications } from '../hooks/useNotifications';
import { useTelemetry } from '../hooks/useTelemetry';
import GuidedTour from './GuidedTour';

const INDUSTRIES = ['Manufacturing', 'Oil and Gas', 'Marine', 'Built Environment', 'Materials and Construction'];

const ROLE_VIEW_ACCESS = {
  Executive: ['cockpit', 'heatmap', 'twin', 'emissions', 'oee', 'cbam', 'reports', 'governance', 'alerts', 'admin'],
  Operations: ['cockpit', 'heatmap', 'twin', 'emissions', 'oee', 'cbam', 'reports', 'alerts'],
  Sustainability: ['cockpit', 'heatmap', 'emissions', 'oee', 'cbam', 'reports', 'governance', 'alerts'],
  Finance: ['cockpit', 'heatmap', 'alerts'],
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [industry, setIndustry] = useState('Manufacturing');
  const [activeView, setActiveView] = useState('cockpit');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [industryContext, setIndustryContext] = useState(null);
  const [drilldownSite, setDrilldownSite] = useState(null);
  const [drilldownIndustry, setDrilldownIndustry] = useState(null);
  const [showThresholds, setShowThresholds] = useState(false);

  const {
    notifications,
    unreadCount,
    newBreaches,
    markRead,
    markAllRead,
    dismissToast,
    dismissAllToasts,
    refetch: refetchNotifications,
  } = useNotifications();

  const { trackPageView, trackFeature } = useTelemetry();

  // Guided tour state
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    if (user && user.tour_completed === false) {
      const timer = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Track page navigation
  useEffect(() => { trackPageView(activeView); }, [activeView, trackPageView]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [kpiRes, summaryRes, sitesRes] = await Promise.all([
        getKpis(industry),
        getDashboardSummary(industry),
        getSites(industry),
      ]);
      setKpis(kpiRes.data.kpis);
      setSummary(summaryRes.data);
      setSites(sitesRes.data.sites);
      setIndustryContext(kpiRes.data.industry_context || null);
    } catch (err) {
      console.error('Failed to load dashboard data', err);
    } finally {
      setLoading(false);
    }
  }, [industry]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setDrilldownSite(null); setDrilldownIndustry(null); }, [industry, activeView]);

  // Guard: redirect to cockpit if role doesn't have access to current view
  useEffect(() => {
    const role = user?.role || 'Executive';
    const allowed = ROLE_VIEW_ACCESS[role] || ROLE_VIEW_ACCESS['Executive'];
    if (!allowed.includes(activeView)) {
      setActiveView('cockpit');
    }
  }, [activeView, user?.role]);

  const handleSiteDrilldown = (siteIndustry, siteName) => {
    if (siteIndustry !== industry) {
      setIndustry(siteIndustry);
    }
    setDrilldownIndustry(siteIndustry);
    setDrilldownSite(siteName);
  };

  const canConfigureThresholds = ['Executive', 'Operations'].includes(user?.role);

  const getView = () => {
    if (drilldownSite) {
      return (
        <SiteDrilldown
          industry={drilldownIndustry || industry}
          site={drilldownSite}
          onBack={() => { setDrilldownSite(null); setDrilldownIndustry(null); }}
        />
      );
    }
    switch (activeView) {
      case 'cockpit':
        return (
          <ExecutiveCockpit
            kpis={kpis}
            summary={summary}
            industry={industry}
            industryContext={industryContext}
            loading={loading}
            sites={sites}
            onSiteClick={(site) => { setDrilldownIndustry(industry); setDrilldownSite(site); }}
            onOpenThresholds={canConfigureThresholds ? () => setShowThresholds(true) : null}
          />
        );
      case 'heatmap':
        return <EnterpriseHeatMap onSiteDrilldown={handleSiteDrilldown} />;
      case 'twin':
        return <DigitalTwinPanel industry={industry} sites={sites} />;
      case 'emissions':
        return <EmissionInventory trackFeature={trackFeature} />;
      case 'reports':
        return <ReportsHub />;
      case 'oee':
        return <OeeModule trackFeature={trackFeature} />;
      case 'cbam':
        return <CbamModule trackFeature={trackFeature} />;
      case 'admin':
        return <AdminConsole />;
      case 'governance':
        return <GovernanceDashboard industry={industry} summary={summary} loading={loading} />;
      case 'alerts':
        return <AlertsPanel industry={industry} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden" data-testid="dashboard">
      <Sidebar
        activeView={activeView}
        setActiveView={setActiveView}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        userRole={user?.role}
        onStartTour={() => setShowTour(true)}
      />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Header
          user={user}
          industry={industry}
          setIndustry={setIndustry}
          industries={INDUSTRIES}
          onMenuClick={() => setSidebarOpen(true)}
          onLogout={logout}
          notifications={notifications}
          unreadCount={unreadCount}
          markRead={markRead}
          markAllRead={markAllRead}
          refetchNotifications={refetchNotifications}
        />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          {getView()}
        </main>
      </div>

      <ThresholdConfig
        industry={industry}
        isOpen={showThresholds}
        onClose={() => setShowThresholds(false)}
      />

      <NotificationToasts
        breaches={newBreaches}
        onDismiss={dismissToast}
        onDismissAll={dismissAllToasts}
      />

      <GuidedTour
        isOpen={showTour}
        onClose={() => setShowTour(false)}
        userRole={user?.role}
      />
    </div>
  );
}
